import { motion } from 'framer-motion/dist/framer-motion';
import { CustomPagination } from 'pages/home/Home.style';
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import SwiperCore, { A11y, Lazy, Navigation, Pagination } from 'swiper';
import 'swiper/components/effect-coverflow/effect-coverflow.scss';
import 'swiper/components/navigation/navigation.scss';
import 'swiper/components/pagination/pagination.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.scss';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { H2Text } from 'pages/charterQuote/components/contents/Content.style';

SwiperCore.use([Lazy, Navigation, A11y, Pagination]);

const testimonials = [
  {
    title: 'Ready to request a price quote for your private jet charter?',
    text: 'From entering your trip details to selecting the ideal aircraft, booking your private jet has never been easier.',
    images: [
      {
        src: '/imgs/webp/charterQuote/request-0.webp',
        alt: 'cheap private jet charters',
      },
    ],
  },
  {
    title: 'Enter your trip details',
    text: 'Start by providing your origin, destination, and preferred travel date. This will help us tailor your private plane charter quote to your specific journey.',
    images: [
      {
        src: '/imgs/webp/charterQuote/request-1.webp',
        alt: 'cheap private jet charters',
      },
    ],
  },
  {
    title: 'Choose your Aircraft type',
    text: 'Select the aircraft that best suits your needs, whether you prefer a very light jet for a quick trip or a larger one for private jet travel with more comfort and amenities.',
    images: [
      {
        src: '/imgs/webp/charterQuote/request-2.webp',
        alt: 'luxury private jet charters',
      },
    ],
  },
  {
    title: 'Submit your contact information',
    text: 'After selecting your aircraft, enter your contact details so we can reach out to you.',
    images: [
      {
        src: '/imgs/webp/charterQuote/request-3.webp',
        alt: 'private jet rental',
      },
    ],
  },
  {
    title: 'Receive your Quote',
    text: 'An Airacer flight consultant will contact you within 24 hours with an accurate air charter quote for your flight.',
    images: [
      {
        src: '/imgs/webp/charterQuote/request-4.webp',
        alt: 'private charter jet',
      },
    ],
  },
  {
    title: 'Book your flight',
    text: "Once you’ve reviewed your custom jet charter quote, confirm your booking, and you're all set to fly in luxury and style! ",
    images: [
      {
        src: '/imgs/webp/charterQuote/request-5.webp',
        alt: 'fly charter flight',
      },
    ],
  },
];

const TestContainer = styled.div`
  width: 100%;
  .swiper-slide {
    font-size: 18px;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    width: clamp(279px, 100%, 415px);
    height: 428px;
    @media (max-width: 757px) {
      width: 340px;
      height: 167px;
    }
  }
  @media (max-width: 1380px) {
    padding-right: 0;
  }

  @media (max-width: 757px) {
    margin-top: 24px;
    margin-bottom: 48px;
  }
`;

const CustomerItem = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 376px;
  height: 100%;
  gap: 16px;
  cursor: pointer;
  padding: 16px;
  background: #f4f6fc;
  border-radius: 2px;
  & > * {
    flex: 1;
  }
`;

const TextContent = styled(motion.div)`
  display: flex;
  justify-content: flex-start;
  align-items: space-between;
  flex-direction: column;
  font-weight: 400;
  font-size: 24px;
  color: #050a30;
  line-height: 1.3;
  gap: 16px;
  p {
    font-size: 16px;
    max-height: 500px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 9;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }
`;

const ImageContent = styled.div`
  position: relative;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Image = styled(motion.img)`
  border-radius: 2px;
  object-fit: cover;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 50px;
`;

const Title = styled.h2`
  font-weight: 600;
  font-size: 40px;
  margin: 0;
  line-height: normal;
`;

const Control = styled.div`
  width: fit-content;
  display: flex;
  gap: 16px;
`;

const ControlButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  cursor: pointer;
  background: #f4f6fc;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SquarePointer = styled(motion.div)`
  border: 2px solid #07080a;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  & > img {
    width: 24px;
    height: 24px;
  }
`;

const TextContentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 6px;
  span {
    font-weight: 600;
    font-size: 20px;
  }
`;

const iconMotion = {
  hover: {
    backgroundColor: '#07080a',
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

export const CirclePointerIcon = (props) => {
  return (
    <SquarePointer variants={iconMotion} {...props}>
      <motion.svg
        initial={{ color: '#07080a' }}
        exit={{ color: '#07080a' }}
        variants={{
          hover: {
            color: '#fff',
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        }}
        width="13"
        height="22"
        viewBox="0 0 13 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.48535 19.4852L10.9706 10.9999L2.48535 2.51467"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.svg>
    </SquarePointer>
  );
};

const Request = () => {
  const sliderRef = useRef(null);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  return (
    <TestContainer>
      <Header>
        <H2Text>
          Requesting a Private Jet
          <br /> Charter Quote
        </H2Text>
        <Control>
          <ControlButton onClick={handlePrev}>
            <ArrowLeft color="#050A30" strokeWidth={1.5} />
          </ControlButton>
          <ControlButton onClick={handleNext}>
            <ArrowRight color="#050A30" strokeWidth={1.5} />
          </ControlButton>
        </Control>
        <CustomPagination id="custom-pagination" />
      </Header>

      <Swiper
        slidesPerView={3}
        slidesPerGroup={3}
        spaceBetween={50}
        lazy
        loop
        ref={sliderRef}
        pagination={{
          renderCustom: function (_, current, total) {
            return `<span>${current}</span> / <span>${total}</span>`;
          },
          type: 'custom',
          el: '#custom-pagination',
        }}
      >
        {testimonials.map((item, i) => {
          return (
            <SwiperSlide key={i}>
              <CustomerItem>
                <TextContent>
                  <TextContentHeader>
                    <span>0{i + 1}.</span>
                    <span>{item.title}</span>
                  </TextContentHeader>
                  <p>{item.text}</p>
                </TextContent>

                <ImageContent>
                  <Image src={item.images[0].src} alt={item.images[0].alt} />
                </ImageContent>
              </CustomerItem>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </TestContainer>
  );
};

export default Request;
